/**
 * Local error boundary around the live-preview section.
 *
 * The preview renders an arbitrary registered component with whatever
 * props were last seen on the calling page (or the fallback
 * `previewProps`). Any child can throw — invalid nested queries, bad
 * prop types, missing contexts — and we don't want the config page to
 * die along with it. This boundary swaps the subtree for a compact
 * `Alert` and lets the admin keep working on rules and labels.
 *
 * The component is intentionally minimal: one class, one render
 * fallback, no retry button (refreshing the page is the right UX here).
 * The project does not have a shared `ErrorBoundary` to reuse; if one
 * appears later this file can be replaced with a thin re-export.
 */

import { Component, type ErrorInfo, type ReactNode } from 'react';

import { Alert } from '@components/base/feedback';

interface PreviewErrorBoundaryProps {
  readonly children: ReactNode;
}

interface PreviewErrorBoundaryState {
  readonly hasError: boolean;
  readonly error?: Error;
}

/**
 * React error boundary that catches render-time errors from the live
 * preview. Uses the legacy class API because React still requires class
 * components for `getDerivedStateFromError` / `componentDidCatch` — the
 * hook alternative does not exist.
 */
export class PreviewErrorBoundary extends Component<
  PreviewErrorBoundaryProps,
  PreviewErrorBoundaryState
> {
  override state: PreviewErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(error: Error): PreviewErrorBoundaryState {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Log so we still have a trail in dev tools — production builds
    // should surface this via whatever error-tracking backend the app
    // uses. Kept minimal to avoid the temptation of adding a hard
    // dependency on a logger package.
    console.error('PreviewErrorBoundary caught an error', error, info);
  }

  override render(): ReactNode {
    if (this.state.hasError) {
      return (
        <Alert variant="error" title="Live preview failed">
          The preview of this component threw an error during render. The
          rest of the page still works — fix the component or pick
          different fallback props to restore the preview.
          {this.state.error?.message !== undefined && (
            <>
              <br />
              <code>{this.state.error.message}</code>
            </>
          )}
        </Alert>
      );
    }

    return this.props.children;
  }
}
